import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import ShipmentsFormCard from './ShipmentsFormCard';
import Summary from './Summary';
import { initialSummaryState } from './initialState';

const CreateShipmentWizard = ({
  availableDate,
  batchOwnerName,
  dataCompanyId,
  batchVolume,
  onClose,
  originId,
  originName,
  productId,
  refetchBatchData,
}) => {
  const { batchId } = useParams();
  const initialShipmentState = {
    agreementCode: '',
    bookingOrigin: { bookingOriginId: originId, bookingOriginName: originName },
    comments: '',
    location: null,
    shipper: null,
    volume: '',
  };

  const initialState = {
    batchId: parseInt(batchId),
    shipments: [{ ...initialShipmentState }],
    ...initialSummaryState,
  };

  const [step, setStep] = useState(1);
  const [shipmentState, setShipmentState] = useState(initialState);
  const [comparisonState, setComparisonState] = useState(initialState);

  const initialValidationState = {
    comments: '',
    shipper: '',
    volume: '',
  };

  const [validationMessages, setValidationMessages] = useState([initialValidationState]);
  const [locationHelperText, setLocationHelperText] = useState(['']);

  const [confirmDialogOpened, setConfirmDialogOpened] = useState(false);

  const handleNext = () => {
    setStep(R.inc);
  };
  const handlePrev = () => {
    setStep(R.dec);
  };
  const handleReset = () => {
    setShipmentState(initialState);
    setComparisonState(initialState);
    setValidationMessages([initialValidationState]);
    setStep(1);
  };

  const handleClose = () => {
    if (R.equals(shipmentState, comparisonState)) {
      onClose();
    } else {
      setConfirmDialogOpened(true);
    }
  };

  const handleComplete = () => {
    onClose();
    refetchBatchData();
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpened(false);
  };

  const [totalProposedVolume, setTotalProposedVolume] = useState(0);
  const formattedStringToInt = value => parseInt(R.replace(/[^0-9]/g, '', value));

  useEffect(() => {
    const { shipments } = shipmentState;
    const shipmentsTotalVolume = R.reduce(
      (accum, item) => accum + (item.volume ? formattedStringToInt(item.volume) : 0),
      0
    );
    const proposedVolume = R.add(batchVolume, shipmentsTotalVolume(shipments));
    setTotalProposedVolume(proposedVolume);

    // we don't want to re-total distributed volume
    // after successful API create (or it would be incorrect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentState]);

  const stepProps = {
    initialShipmentState,
    initialValidationState,
    locationHelperText,
    onClose: handleClose,
    onComplete: handleComplete,
    onNext: handleNext,
    onPrev: handlePrev,
    onReset: handleReset,
    refetchBatchData,
    setComparisonState,
    setLocationHelperText,
    setShipmentState,
    setValidationMessages,
    shipmentState,
    totalProposedVolume,
    validationMessages,
  };

  const shipmentProps = {
    availableDate,
    batchOwnerName,
    dataCompanyId,
    originId,
    originName,
    productId,
  };

  return (
    <Dialog
      fullScreen
      onClose={handleClose}
      open
      slotProps={{
        paper: { elevation: 0 },
      }}
    >
      {step === 1 && <ShipmentsFormCard {...stepProps} {...shipmentProps} />}
      {step === 2 && <Summary {...stepProps} {...shipmentProps} />}
      {confirmDialogOpened && (
        <Dialog
          maxWidth="xs"
          onClose={handleCloseConfirmDialog}
          open
          slotProps={{
            paper: { elevation: 10 },
          }}
          sx={{ '& .MuiDialog-paper': { height: 180, width: 330 } }}
        >
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogContent>
            Are you sure you want to leave this page? You have unsaved changes.
          </DialogContent>
          <DialogActions sx={{ pb: 2, px: 2 }}>
            <Button autoFocus onClick={onClose} variant="outlined">
              Continue
            </Button>
            <Button onClick={handleCloseConfirmDialog}>Keep Editing</Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

CreateShipmentWizard.propTypes = {
  availableDate: PropTypes.string.isRequired,
  batchOwnerName: PropTypes.string.isRequired,
  batchVolume: PropTypes.number.isRequired,
  dataCompanyId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  originId: PropTypes.number.isRequired,
  originName: PropTypes.string.isRequired,
  productId: PropTypes.number.isRequired,
  refetchBatchData: PropTypes.func.isRequired,
};

export default CreateShipmentWizard;
